import React, { useState, useEffect } from 'react';
import { Users, Wrench, Heart, Microscope, Radio, Plus, X, Zap } from 'lucide-react';
import { THEMES, getColorClasses } from '../pages/themeConfig';

const BaseModules = ({ 
  resources, 
  setResources, 
  facilities, 
  currentCampaign, 
  apiHandlers, 
  theme = 'vanilla' 
}) => {
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [buildMenuOpen, setBuildMenuOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const config = THEMES[theme] || THEMES.vanilla;
  
  // Theme color configuration (mirrors BaseHeader)
  const colorConfig = {
    amber: { 
      bg: 'linear-gradient(180deg, rgba(180, 83, 9, 0.45) 0%, rgba(120, 53, 15, 0.4) 100%)', 
      border: 'rgba(180, 83, 9, 0.5)', 
      glow: 'rgba(180, 83, 9, 0.1)' 
    },
    orange: { 
      bg: 'linear-gradient(180deg, rgba(194, 65, 12, 0.45) 0%, rgba(124, 45, 18, 0.4) 100%)', 
      border: 'rgba(194, 65, 12, 0.5)', 
      glow: 'rgba(194, 65, 12, 0.1)' 
    },
    cyan: { 
      bg: 'linear-gradient(180deg, rgba(6, 182, 212, 0.45) 0%, rgba(15, 118, 110, 0.4) 100%)', 
      border: 'rgba(6, 182, 212, 0.5)', 
      glow: 'rgba(6, 182, 212, 0.1)' 
    },
    blue: { 
      bg: 'linear-gradient(180deg, rgba(59, 130, 246, 0.45) 0%, rgba(37, 99, 235, 0.4) 100%)', 
      border: 'rgba(59, 130, 246, 0.5)', 
      glow: 'rgba(59, 130, 246, 0.1)' 
    },
    purple: { 
      bg: 'linear-gradient(180deg, rgba(168, 85, 247, 0.45) 0%, rgba(126, 34, 206, 0.4) 100%)', 
      border: 'rgba(168, 85, 247, 0.5)', 
      glow: 'rgba(168, 85, 247, 0.1)' 
    },
    emerald: { 
      bg: 'linear-gradient(180deg, rgba(16, 185, 129, 0.45) 0%, rgba(5, 150, 105, 0.4) 100%)', 
      border: 'rgba(16, 185, 129, 0.5)', 
      glow: 'rgba(16, 185, 129, 0.1)' 
    }
  };
  
  const colors = colorConfig[config.colors.primary] || colorConfig.blue;
  
  // Dynamic background based on theme
  const backgroundImage = `/images/base_background_${theme}.png`;

  const [baseGrid, setBaseGrid] = useState([]);

  useEffect(() => {
    const grid = Array(5).fill(null).map(() => Array(5).fill(null));
    
    facilities.forEach(facility => {
      if (facility.grid_row !== null && facility.grid_col !== null) {
        if (facility.is_active === true || facility.is_active === undefined) {
          // Use themed facility name if available
          const themedName = config.facilities?.[facility.facility_type] || facility.facility_name;
          
          grid[facility.grid_row][facility.grid_col] = {
            id: facility.id,
            type: facility.facility_type,
            name: themedName,
            level: facility.level,
            built: true
          };
        }
      }
    });

    setBaseGrid(grid);
  }, [facilities, config]);

  const facilityTypes = {
    command: { icon: Radio, color: config.colors.primary, desc: 'Strategic operations hub' },
    barracks: { icon: Users, color: 'green', desc: 'Train and house soldiers' },
    generator: { icon: Zap, color: 'blue', desc: 'Generate power for base operations' },
    medbay: { icon: Heart, color: 'red', desc: 'Heal wounded soldiers' },
    research: { icon: Microscope, color: config.colors.secondary, desc: 'Unlock new technology' },
    workshop: { icon: Wrench, color: config.colors.accent, desc: 'Craft items and upgrades' }
  };

  // Calculate total power available based on generators
  const calculateTotalPowerCapacity = () => {
    const generatorCount = facilities.filter(f => f.facility_type === 'generator' && (f.is_active === true || f.is_active === undefined)).length;
    const basePowerPerGenerator = 100;
    return generatorCount * basePowerPerGenerator;
  };

  // Calculate total power consumption from other facilities
  const calculatePowerConsumption = () => {
    const facilityPowerCosts = {
      barracks: 10,
      medbay: 20,
      research: 25,
      workshop: 15,
      command: 5
    };

    return facilities
      .filter(f => f.facility_type !== 'generator' && (f.is_active === true || f.is_active === undefined))
      .reduce((total, facility) => total + (facilityPowerCosts[facility.facility_type] || 0), 0);
  };

  // Calculate remaining power
  const totalPowerCapacity = calculateTotalPowerCapacity();
  const powerConsumption = calculatePowerConsumption();
  const remainingPower = totalPowerCapacity - powerConsumption;

  const getThemeSpecificRooms = () => {
    const baseRooms = [
      { type: 'barracks', cost: { funds: 5000, supplies: 50 }, power: 10 },
      { type: 'generator', cost: { funds: 7500, supplies: 50 }, power: 0 },
      { type: 'medbay', cost: { funds: 6000, supplies: 60 }, power: 20 },
      { type: 'research', cost: { funds: 10000, supplies: 100 }, power: 25 },
      { type: 'workshop', cost: { funds: 8000, supplies: 80 }, power: 15 }
    ];
    
    return baseRooms.map(room => ({
      ...room,
      name: config.facilities[room.type] || room.type
    }));
  };

  const buildableRooms = getThemeSpecificRooms();

  const handleBuildRoom = async (roomType) => {
    if (!selectedSlot || !currentCampaign?.id) return;

    const room = buildableRooms.find(r => r.type === roomType);
    if (!room) return;

    if (baseGrid[selectedSlot.row]?.[selectedSlot.col]) {
      alert('This slot is already occupied!');
      return;
    }

    if (resources.funds < room.cost.funds || resources.supplies < room.cost.supplies) {
      alert('Insufficient resources!');
      return;
    }

    // Check if building this facility would exceed power capacity
    if (roomType !== 'generator' && remainingPower < room.power) {
      alert('Insufficient power! Build a Power Generator to increase capacity.');
      return;
    }

    try {
      await apiHandlers.createFacility(
        room.type,
        room.name,
        selectedSlot.row,
        selectedSlot.col,
        1
      );

      setResources(prev => ({
        ...prev,
        funds: prev.funds - room.cost.funds,
        supplies: prev.supplies - room.cost.supplies
      }));

      setSelectedSlot(null);
      setBuildMenuOpen(false);
    } catch (error) {
      const message = error.message?.includes('UNIQUE constraint')
        ? 'This position is already occupied! Please refresh the page to see all facilities.'
        : `Failed to build facility: ${error.message}`;
      alert(message);
    }
  };

  const handleUpgrade = async () => {
    if (!selectedRoom) return;

    const upgradeCost = {
      funds: 5000 * selectedRoom.level,
      supplies: 50 * selectedRoom.level
    };

    if (resources.funds < upgradeCost.funds || resources.supplies < upgradeCost.supplies) {
      alert('Insufficient resources to upgrade!');
      return;
    }

    try {
      await apiHandlers.upgradeFacility(selectedRoom.id, selectedRoom.level + 1);

      setResources(prev => ({
        ...prev,
        funds: prev.funds - upgradeCost.funds,
        supplies: prev.supplies - upgradeCost.supplies
      }));

      setSelectedRoom(null);
    } catch (error) {
      alert('Failed to upgrade facility');
    }
  };

  const handleSlotClick = (row, col, room) => {
    if (room) {
      setSelectedRoom(room);
    } else {
      setSelectedSlot({ row, col });
      setBuildMenuOpen(true);
    }
  };

  return (
    <div>
      {buildMenuOpen && (
        <div className="border-2 rounded-lg p-6 mb-6" style={{
          background: colors.bg,
          borderColor: colors.border,
          boxShadow: `inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 4px 12px ${colors.glow}`
        }}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold">
              {selectedSlot 
                ? `Build at position (${selectedSlot.row}, ${selectedSlot.col})`
                : 'Select an empty slot to build'}
            </h3>
            <button onClick={() => {
              setBuildMenuOpen(false);
              setSelectedSlot(null);
            }}>
              <X className="text-gray-400 hover:text-white" />
            </button>
          </div>
          <div className="grid grid-cols-5 gap-4">
            {buildableRooms.map(room => {
              const Icon = facilityTypes[room.type].icon;
              const canAfford = resources.funds >= room.cost.funds && 
                               resources.supplies >= room.cost.supplies;
              
              // Generators don't consume power, other facilities do
              const hasPower = room.type === 'generator' || remainingPower >= room.power;
              const canBuild = canAfford && hasPower;
              
              return (
                <button
                  key={room.type}
                  onClick={() => selectedSlot && handleBuildRoom(room.type)}
                  disabled={!selectedSlot || !canBuild}
                  className={`bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded-lg p-4 text-center transition-all
                    ${!canBuild ? 'opacity-50 cursor-not-allowed' : ''}
                    ${!selectedSlot ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <Icon className={`${getColorClasses(facilityTypes[room.type].color)} mx-auto mb-2`} size={32} />
                  <p className="font-bold text-sm mb-2">{room.name}</p>
                  <p className="text-xs text-gray-400 mb-1">${room.cost.funds.toLocaleString()}</p>
                  <p className="text-xs text-yellow-400 mb-1">📦 {room.cost.supplies}</p>
                  <p className="text-xs text-purple-400">⚡ {room.type === 'generator' ? '+100 PWR' : `${room.power} PWR`}</p>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="rounded-lg p-8 border-2 relative overflow-hidden" style={{
        background: colors.bg,
        borderColor: colors.border,
        boxShadow: `inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 8px 24px ${colors.glow}`
      }}>
        {/* Subtle top highlight */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
        
        {/* Noise texture overlay */}
        <div className="absolute inset-0 opacity-5 pointer-events-none" style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox="0 0 400 400" xmlns="http://www.w3.org/2000/svg"%3E%3Cfilter id="noiseFilter"%3E%3CfeTurbulance type="fractalNoise" baseFrequency="0.9" numOctaves="4" seed="2"/%3E%3C/filter%3E%3Crect width="400" height="400" fill="%23000" filter="url(%23noiseFilter)"/%3E%3C/svg%3E")'
        }} />
        
        {/* Background Image */}
        {backgroundImage && (
          <div 
            className="absolute inset-0 opacity-20 bg-cover bg-center"
            style={{ backgroundImage: `url(${backgroundImage})` }}
          />
        )}
        
        {/* Grid content with relative positioning to appear above background */}
        <div className="relative z-10 space-y-4 w-[1200px] mx-auto">
          {baseGrid.map((row, rowIndex) => (
            <div key={rowIndex} className="flex gap-4">
              {row.map((room, colIndex) => (
                <div key={colIndex} className="w-[224px]">
                  {room ? (
                    <button
                      onClick={() => handleSlotClick(rowIndex, colIndex, room)}
                      className="w-full h-32 rounded-lg hover:scale-105 transition-transform relative overflow-hidden bg-cover bg-center"
                      style={{
                        backgroundImage: `url(/images/modules/${room.type}_module.png)`
                      }}
                    >
                      {/* Dark overlay for better text readability */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent" />
                      
                      {/* Text content */}
                      <div className="absolute bottom-4 left-0 right-0 flex justify-center">
                        <p className="text-xs text-gray-200 drop-shadow-md">Level {room.level}</p>
                      </div>
                    </button>
                  ) : (
                    <button
                      onClick={() => handleSlotClick(rowIndex, colIndex, null)}
                      className="w-full h-32 border-2 border-dashed border-gray-700 rounded-lg flex items-center justify-center hover:border-gray-500 transition-colors"
                    >
                      <Plus className="text-gray-600" size={32} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {selectedRoom && (
        <div className="mt-6 border-2 rounded-lg p-6 relative" style={{
          background: colors.bg,
          borderColor: colors.border,
          boxShadow: `inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 8px 24px ${colors.glow}`
        }}>
          {/* Subtle top highlight */}
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
          
          <div className="flex items-start justify-between relative z-10">
            <div>
              <h3 className="text-2xl font-bold mb-2">{selectedRoom.name}</h3>
              <p className="text-gray-400 mb-4">{facilityTypes[selectedRoom.type].desc}</p>
              <div className="mb-4">
                <p className="text-sm text-gray-400">Current Level: {selectedRoom.level}</p>
                <p className="text-sm text-yellow-400">
                  Upgrade Cost: ${(5000 * selectedRoom.level).toLocaleString()} | 
                  📦 {50 * selectedRoom.level}
                </p>
              </div>
              <div className="flex gap-4">
                <button 
                  onClick={handleUpgrade}
                  className={`${getColorClasses(config.colors.primary, 'bgMain')} ${getColorClasses(config.colors.primary, 'bgHover')} px-6 py-2 rounded font-bold transition-colors`}
                >
                  UPGRADE (Level {selectedRoom.level + 1})
                </button>
                <button className="bg-gray-700 hover:bg-gray-600 px-6 py-2 rounded font-bold transition-colors">
                  VIEW DETAILS
                </button>
              </div>
            </div>
            <button onClick={() => setSelectedRoom(null)}>
              <X className="text-gray-400 hover:text-white" size={24} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BaseModules;